/**
 * Плейсхолдери промпту — §3.
 *
 * Список живе в контрактах, а не в сервісі генерації, бо його читають з двох боків: сервер
 * валідує ним тіло промпту на збереженні, а екран промптів показує ним палітру. Дві копії
 * розійшлися б у той самий день, коли хтось додасть змінну, і оператор отримав би відмову
 * «невідомий плейсхолдер» на змінну, яку йому запропонувала сама CRM.
 */

/** Те, що контекст генерації взагалі вміє підставити. */
export const KNOWN_PLACEHOLDERS = [
  'brand',
  'brand_url',
  'geo',
  'geo_name',
  'language',
  'currency',
  'main_kw',
  'keywords',
  'bonus_list',
  'bonus_main',
  'license',
  'license_number',
  'legal_entity',
  'payments',
  'providers',
  'games_top',
  'min_age',
  'rg_helpline',
  'year',
  'site_domain',
  'page_type',
  'target_words',
] as const;

export type PromptPlaceholder = (typeof KNOWN_PLACEHOLDERS)[number];

/**
 * Промпт без сутності й без ринку генерує однаковий текст для всієї сітки — рівно те, заради
 * чого продукт існує.
 */
export const REQUIRED_PLACEHOLDERS = ['brand', 'geo'] as const;

/** Підписи палітри: оператор обирає «Бонуси списком», а не `{bonus_list}`. */
export const PLACEHOLDER_LABELS: Record<PromptPlaceholder, string> = {
  brand: 'Бренд',
  brand_url: 'URL бренду',
  geo: 'Код ГЕО',
  geo_name: 'Назва країни',
  language: 'Мова',
  currency: 'Валюта',
  main_kw: 'Головний ключ',
  keywords: 'Ключі сторінки',
  bonus_list: 'Бонуси списком',
  bonus_main: 'Головний бонус',
  license: 'Ліцензія',
  license_number: 'Номер ліцензії',
  legal_entity: 'Юрособа у футері',
  payments: 'Платіжні методи',
  providers: 'Провайдери',
  games_top: 'Топ ігор',
  min_age: 'Мінімальний вік',
  rg_helpline: 'Хелплайн RG',
  year: 'Рік',
  site_domain: 'Домен сайту',
  page_type: 'Тип сторінки',
  target_words: 'Цільовий обсяг',
};

/**
 * Плейсхолдери, під які в моделі немає джерела.
 *
 * `renderPrompt` лишає нерозвʼязане видимим — у промпт доїжджає літерал `{games_top}`, і
 * модель пише навколо нього. Тобто «відома змінна» й «змінна, яку є з чого підставити» —
 * різні речі, і мовчазна різниця між ними коштувала б абзацу з дужками на живій сторінці.
 *
 * `games_top` тут тому, що звʼязку «бренд ↔ гра» в схемі немає: ні `brand_games`, ні
 * `brand_game_providers`. Поки її немає, топ ігор для конкретного бренду взяти нізвідки.
 *
 * Окремо, і не в цьому наборі: `providers` **розвʼязується, але не звужується брендом** —
 * береться десять провайдерів із довідника без жодного фільтра. З тієї ж причини: звʼязки
 * немає. Значення непорожнє, тож формально плейсхолдер робочий; фактично сторінка бренду
 * перелічує довільних провайдерів. Це модельна діра, а не помилка коду.
 */
export const PLACEHOLDERS_WITHOUT_SOURCE: readonly PromptPlaceholder[] = ['games_top'];
